/**
 * Modal para editar un logro existente (especialidad, maestría o certificación)
 */

import { useState, useEffect } from "react";
import { Button, Input, Badge, SpecialtyImage } from "@/components/ui";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  X,
  Loader2,
  Award,
  User,
  Trash2,
  GraduationCap,
  Medal,
  AlertTriangle,
} from "lucide-react";
import {
  useUpdateAchievement,
  useDeleteAchievement,
} from "@/hooks/useProgress";
import { useMembersByClub } from "@/hooks/usePersons";
import {
  useSpecialties,
  useMaestrias,
  useCertificaciones,
} from "@/hooks/useCatalogs";
import { Timestamp } from "firebase/firestore";
import { todayLocalDateString } from "@/lib/utils";
import type { Achievement } from "@/types";

interface EditAchievementModalProps {
  isOpen: boolean;
  onClose: () => void;
  achievement: Achievement;
}

const achievementTypeLabels: Record<string, string> = {
  specialty: "Especialidad",
  mastery: "Maestría",
  certification: "Certificación",
  class: "Clase",
  investiture: "Investidura",
  position: "Cargo",
};

const achievementTypeColors: Record<string, string> = {
  specialty: "bg-purple-100 text-purple-800",
  mastery: "bg-amber-100 text-amber-800",
  certification: "bg-cyan-100 text-cyan-800",
  class: "bg-green-100 text-green-800",
  investiture: "bg-yellow-100 text-yellow-800",
  position: "bg-gray-100 text-gray-800",
};

export default function EditAchievementModal({
  isOpen,
  onClose,
  achievement,
}: EditAchievementModalProps) {
  const updateMutation = useUpdateAchievement();
  const deleteMutation = useDeleteAchievement();

  const { data: members } = useMembersByClub(achievement.clubId);
  const { data: specialties } = useSpecialties();
  const { data: maestrias } = useMaestrias();
  const { data: certificaciones } = useCertificaciones();

  // Estado del formulario
  const [earnedDate, setEarnedDate] = useState("");
  const [instructorName, setInstructorName] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Obtener info del miembro
  const member = members?.find((m) => m.id === achievement.memberId);
  const memberName = member?.person.fullName || "Miembro";

  // Obtener info del catálogo según el tipo
  const specialtyInfo = specialties?.find(
    (s) => s.id === achievement.catalogId,
  );
  const maestriaInfo = maestrias?.find((m) => m.id === achievement.catalogId);
  const certificacionInfo = certificaciones?.find(
    (c) => c.id === achievement.catalogId,
  );

  // Inicializar valores cuando se abre
  useEffect(() => {
    if (isOpen && achievement) {
      // Convertir Timestamp a string de fecha
      const date = achievement.earnedDate?.toDate?.()
        ? achievement.earnedDate.toDate()
        : new Date(achievement.earnedDate as any);
      setEarnedDate(date.toISOString().split("T")[0]);
      setInstructorName(achievement.instructorName || "");
      setShowDeleteConfirm(false);
    }
  }, [isOpen, achievement]);

  const handleSubmit = async () => {
    try {
      await updateMutation.mutateAsync({
        id: achievement.id,
        data: {
          earnedDate: Timestamp.fromDate(new Date(earnedDate + "T12:00:00")),
          instructorName: instructorName.trim() || undefined,
        },
      });
      onClose();
    } catch (error) {
      console.error("Error updating achievement:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(achievement.id);
      onClose();
    } catch (error) {
      console.error("Error deleting achievement:", error);
    }
  };

  if (!isOpen) return null;

  const isSubmitting = updateMutation.isPending || deleteMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                achievement.type === "specialty"
                  ? "bg-gradient-to-br from-purple-400 to-purple-600 shadow-purple-500/25"
                  : achievement.type === "mastery"
                    ? "bg-gradient-to-br from-amber-400 to-amber-600 shadow-amber-500/25"
                    : "bg-gradient-to-br from-cyan-400 to-cyan-600 shadow-cyan-500/25"
              }`}
            >
              {achievement.type === "specialty" ? (
                <Award className="w-5 h-5 text-white" />
              ) : achievement.type === "mastery" ? (
                <GraduationCap className="w-5 h-5 text-white" />
              ) : (
                <Medal className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Editar Logro
              </h2>
              <Badge className={achievementTypeColors[achievement.type]}>
                {achievementTypeLabels[achievement.type]}
              </Badge>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info del logro (solo lectura) */}
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 space-y-3 border border-gray-100">
            <div className="flex items-center gap-3">
              {achievement.type === "mastery" && maestriaInfo?.localImage ? (
                <img
                  src={maestriaInfo.localImage}
                  alt={achievement.catalogName}
                  className="w-12 h-12 rounded-xl object-contain bg-amber-50"
                />
              ) : achievement.type === "certification" &&
                certificacionInfo?.localImage ? (
                <img
                  src={certificacionInfo.localImage}
                  alt={achievement.catalogName}
                  className="w-12 h-12 rounded-xl object-contain bg-cyan-50"
                />
              ) : achievement.type === "specialty" ? (
                <SpecialtyImage
                  name={achievement.catalogName}
                  categorySlug={specialtyInfo?.categorySlug || ""}
                  size="md"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-200">
                  <Award className="w-6 h-6 text-gray-600" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-gray-900">
                  {achievement.catalogName}
                </h3>
                <p className="text-sm text-gray-500">
                  Este campo no es editable
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span className="font-medium">{memberName}</span>
              <span className="text-gray-400">(no editable)</span>
            </div>
          </div>

          {/* Campos editables */}
          <div className="space-y-4">
            <div>
              <DatePicker
                label="Fecha de Obtención"
                value={earnedDate}
                onChange={setEarnedDate}
                max={todayLocalDateString()}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructor (opcional)
              </label>
              <Input
                placeholder="Nombre del instructor"
                value={instructorName}
                onChange={(e) => setInstructorName(e.target.value)}
              />
            </div>
          </div>

          {/* Zona de peligro */}
          <div className="border-t pt-6">
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 hover:text-red-700 text-sm flex items-center gap-2 transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" />
                Eliminar este logro
              </button>
            ) : (
              <div className="bg-gradient-to-r from-red-50 to-red-100/50 border-2 border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">
                      ¿Estás seguro de eliminar este logro?
                    </p>
                    <p className="text-sm text-red-600 mt-1">
                      Esta acción no se puede deshacer. Se eliminará el registro
                      de <strong>{achievement.catalogName}</strong> para{" "}
                      <strong>{memberName}</strong>.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleDelete}
                        disabled={isSubmitting}
                      >
                        {deleteMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-1" />
                        )}
                        Sí, eliminar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : null}
            Guardar Cambios
          </Button>
        </div>
      </div>
    </div>
  );
}
